import React from "react";
import styled, { css } from "styled-components";
import { narrowScreenMediaQuery } from "../../constants/styles";
import { BlackTheme } from "../../commons/theme_color";
import Section from "../../commons/Section";
import { UserInfoContext } from "../../providers/ProvideUserInfo";
import { unmodule } from "../../util";
import Alert from "../../components/Alert";
import {
  Label,
  LabelContainer,
  LabelText,
  LabelTitle,
} from "../../components/Label";
import { ProgressBar } from "../../components/ProgressBar";

const UserDataCompletion = (props) => {
  const { percentage_profile } = React.use(UserInfoContext);
  const alertMessage = React.useMemo(() => {
    if (percentage_profile < 99) {
      return "Lengkapi data anda untuk administrasi sertifikasi";
    }
  }, [percentage_profile]);
  const [hovered, setHovered] = React.useState(false);
  const handleMouseOver = React.useCallback(() => {
    if (!alertMessage) return;
    setHovered(true);
  }, [alertMessage]);
  const handleMouseOut = React.useCallback(() => {
    if (!alertMessage) return;
    setHovered(false);
  }, [alertMessage]);

  return (
    <ProgressBarContainer
      onMouseOver={handleMouseOver}
      onMouseOut={handleMouseOut}
      {...(percentage_profile < 99 && {
        $hoverable: true,
      })}
    >
      {hovered && <Alert $absolute>{alertMessage}</Alert>}
      <LabelTitle>
        {percentage_profile && (
          <img src={unmodule(require("../../images/icons/warning.svg"))} />
        )}
        Kelengkapan data
      </LabelTitle>
      <ProgressBar progress={percentage_profile} />
      <ProgressText>{percentage_profile}%</ProgressText>
    </ProgressBarContainer>
  );
};

const UnnamedComponent = (props) => {
  const {
    avatar_src,
    birthday,
    city,
    email,
    kk_number,
    name,
    nik,
    phone_number,
    position,
    is_locked,
    locked_at,
    subscribe_days,
  } = React.use(UserInfoContext);

  const fields = React.useMemo(
    () => [
      {
        key: "email",
        text: email,
        iconSrc: unmodule(require("../../images/icons/mail.svg")),
      },
      {
        key: "position",
        text: position,
        iconSrc: unmodule(require("../../images/icons/shield-user.svg")),
      },
      {
        key: "city",
        text: city,
        iconSrc: unmodule(require("../../images/icons/marker.svg")),
      },
      {
        key: "phone_number",
        text: phone_number,
        iconSrc: unmodule(require("../../images/icons/call.svg")),
      },
      {
        key: "birthday",
        text: birthday,
        iconSrc: unmodule(require("../../images/icons/cake.svg")),
      },
      {
        key: "nik",
        text: nik,
        textIcon: "NIK",
      },
      {
        key: "kk",
        text: kk_number,
        textIcon: "KK",
      },
    ],
    [
      email,
      position,
      city,
      phone_number,
      birthday,
      nik,
      kk_number,
      is_locked,
      locked_at,
    ],
  );

  return (
    <BlackTheme>
      <Section $paddingY>
        {is_locked && (
          <Alert>
            Akun Anda di block pada tanggal {locked_at}, Hubungi admin untuk
            info lebih lanjut.
          </Alert>
        )}
        <AvatarAndInfoFlex>
          <Avatar src={avatar_src} />
          <InfoContainer>
            <InfoFlex>
              <UserName>{name}</UserName>
              <UserActionFlex>
                <RiwayatPembayaranLink href="/memberships">
                  Riwayat Pembayaran
                </RiwayatPembayaranLink>
              </UserActionFlex>
            </InfoFlex>
            <InfoFlex $wrap>
              <FieldFlex>
                {fields.map((entry) => (
                  <FieldItem key={entry.key}>
                    {entry.iconSrc ? (
                      <FieldIcon src={entry.iconSrc} />
                    ) : (
                      entry.textIcon && (
                        <FieldIconText>{entry.textIcon}</FieldIconText>
                      )
                    )}
                    {entry.text}
                  </FieldItem>
                ))}
              </FieldFlex>
              <UserActionFlex>
                <LabelContainer>
                  <LabelTitle>Status member:</LabelTitle>
                  {subscribe_days > 0 ? (
                    <Label $success>
                      <img
                        src={unmodule(
                          require("../../images/icons/shield-check.svg"),
                        )}
                      />
                      <LabelText>
                        Tersisa <b>{subscribe_days} hari</b>
                      </LabelText>
                    </Label>
                  ) : (
                    <Label $danger>
                      <img
                        src={unmodule(require("../../images/icons/stop.svg"))}
                      />
                      <LabelText>Non-member</LabelText>
                    </Label>
                  )}
                </LabelContainer>
                <UserDataCompletion />
              </UserActionFlex>
            </InfoFlex>
          </InfoContainer>
        </AvatarAndInfoFlex>
      </Section>
    </BlackTheme>
  );
};

export default UnnamedComponent;

const ProgressBarContainer = styled(LabelContainer)`
  min-width: 150px;
  position: relative;

  ${(props) =>
    props.$hoverable &&
    css`
      cursor: pointer;

      &:hover {
        text-decoration: underline;
        color: #ffc107;
      }
    `};
`;

const ProgressText = styled.div`
  font-size: 14px;
  font-weight: bold;
`;

const InfoContainer = styled.div`
  width: 100%;
`;

const AvatarAndInfoFlex = styled.div`
  align-items: start;
  border-bottom: 1px solid white;
  display: flex;
  gap: 20px;

  @media ${narrowScreenMediaQuery} {
    flex-wrap: wrap;
    align-items: center;
  }
`;

const FieldIconText = styled.div`
  border: 1px solid #ffffff45;
  font-weight: bold;
  margin-right: 4px;
  padding: 2px;
`;

const FieldItem = styled.div`
  align-items: center;
  cursor: pointer;
  display: flex;
  min-width: 50px;
`;

const UserName = styled.div`
  flex: 1 1 0;
  font-size: 20px;
`;

const FieldFlex = styled.div`
  color: #9ea3bb;
  display: flex;
  flex-wrap: wrap;
  flex: 1 1 0;
  font-size: 14px;
  gap: 15px;
`;

const UserActionFlex = styled.div`
  display: flex;
  flex-wrap: wrap;
  flex: 1 1 0;
  gap: 20px;
  justify-content: end;

  @media ${narrowScreenMediaQuery} {
    flex: unset;
    justify-content: unset;
  }
`;

const FieldIcon = styled.img`
  margin-right: 2px;
`;

const Avatar = styled.img`
  border-radius: 5px;
  height: 120px;
  object-fit: cover;
  width: 120px;

  @media ${narrowScreenMediaQuery} {
    height: 200px;
    margin-left: auto;
    margin-right: auto;
    width: 200px;
  }
`;

const InfoFlex = styled.div`
  align-items: start;
  display: flex;
  gap: 30px;
  margin-bottom: 25px;

  ${(props) =>
    props.$wrap &&
    css`
      flex-wrap: wrap;
    `};
`;

const RiwayatPembayaranLink = styled.a`
  background-color: #ffa80078;
  border-radius: 4px;
  border: 1px solid #ffa800;
  color: #ffffff;
  display: block;
  font-size: 14px;
  padding: 7px 6px;
  text-decoration: none;

  &:hover {
    background-color: #ffa800bd;
  }
`;
